import React, { useState } from 'react';
import { Heart, MessageCircle, Bell, BellOff, Star } from 'lucide-react';
interface Angel {
  id: string;
  name: string;
  username: string;
  avatar: string;
  tagline: string;
  membershipStatus?: string;
  online?: boolean;
  newContent?: boolean;
  nsfwStatus?: string;
  followers?: number;
}
interface AngelCardProps {
  angel: Angel;
  viewMode: 'grid' | 'list';
  onClick: () => void;
}
export const AngelCard: React.FC<AngelCardProps> = ({
  angel,
  viewMode,
  onClick
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const handleFollow = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFollowing(!isFollowing);
  };
  const handleMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Handle messaging logic
    console.log(`Message ${angel.name}`);
  };
  if (viewMode === 'list') {
    return <div className="flex items-center gap-4 p-3 bg-white dark:bg-black rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-100" onClick={onClick} data-id="element-1320">
        <div className="relative" data-id="element-1321">
          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-pink-500" data-id="element-1322">
            <img src={angel.avatar} alt={angel.name} className="w-full h-full object-cover" data-id="element-1323" />
          </div>
          {angel.online && <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-900" data-id="element-1324"></div>}
        </div>
        <div className="flex-1" data-id="element-1325">
          <div className="flex items-center gap-2" data-id="element-1326">
            <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1327">
              {angel.name}
            </h3>
            {angel.newContent && <span className="bg-pink-500 text-white text-xs px-1.5 py-0.5 rounded-full" data-id="element-1328">
                New
              </span>}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1329">
            {angel.tagline}
          </p>
          <div className="flex items-center gap-2 mt-1" data-id="element-1330">
            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1331">
              {angel.followers?.toLocaleString() || '0'} followers
            </span>
            {angel.nsfwStatus && <span className="text-xs" data-id="element-1332">{angel.nsfwStatus}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-1333">
          <button className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${isFollowing ? 'bg-pink-100 dark:bg-pink-900 text-pink-500' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`} onClick={handleFollow} title={isFollowing ? 'Unfollow' : 'Follow'} data-id="element-1334">
            {isFollowing ? <BellOff size={18} className="text-pink-500" data-id="element-1335" /> : <Bell size={18} data-id="element-1336" />}
          </button>
          <button className="w-9 h-9 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" onClick={handleMessage} title="Message" data-id="element-1337">
            <MessageCircle size={18} data-id="element-1338" />
          </button>
        </div>
      </div>;
  }
  return <div className="relative rounded-lg overflow-hidden cursor-pointer transition-all duration-300 shadow-sm hover:shadow-md" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={onClick} data-id="element-1339">
      <div className="aspect-square relative" data-id="element-1340">
        {/* Avatar image */}
        <img src={angel.avatar} alt={angel.name} className="w-full h-full object-cover transition-transform duration-500" data-id="element-1341" />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-1342"></div>
        {/* Status indicators */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1" data-id="element-1343">
          {angel.online && <div className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold shadow-lg animate-pulse" data-id="element-1344">
              <span className="w-2 h-2 bg-white rounded-full" data-id="element-1345"></span>
              <span data-id="element-1346">LIVE</span>
            </div>}
          {angel.newContent && <div className="bg-pink-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold shadow-lg" data-id="element-1347">
              NEW
            </div>}
        </div>
        {/* Info overlay */}
        <div className="absolute inset-x-0 bottom-0 p-3 flex flex-col justify-end" data-id="element-1348">
          <h3 className="text-white font-semibold text-lg drop-shadow-md" data-id="element-1349">
            {angel.name}
          </h3>
          <p className="text-white/90 text-sm drop-shadow-md line-clamp-2" data-id="element-1350">
            {angel.tagline}
          </p>
          {/* Action buttons (visible on hover) */}
          {isHovered && <div className="flex justify-between items-center mt-3 animate-in fade-in duration-200" data-id="element-1351">
              <div className="flex gap-2" data-id="element-1352">
                <button className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-2 transition-all duration-200 hover:scale-110" onClick={handleFollow} data-id="element-1353">
                  {isFollowing ? <Heart size={18} className="text-pink-500 fill-pink-500" data-id="element-1354" /> : <Heart size={18} data-id="element-1355" />}
                </button>
                <button className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-2 transition-all duration-200 hover:scale-110" onClick={handleMessage} data-id="element-1356">
                  <MessageCircle size={18} className="text-blue-500" data-id="element-1357" />
                </button>
              </div>
              {angel.followers && <span className="text-white/90 text-xs bg-black/30 px-2 py-1 rounded-full" data-id="element-1358">
                  {angel.followers.toLocaleString()} followers
                </span>}
            </div>}
        </div>
      </div>
    </div>;
};